import { useCallback, useMemo, useState } from "react"

import { useGetInventoryPalletGroups, useUntestedLotsValidation } from "../../../hooks"
import type { InventoryLotReference, InventoryLotWithPallet } from "../../../types"

import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import type { InventoryPalletGroup } from "@/graphql/codegen/graphql"
import { InventoryPalletGroupsSortField, SortingOrder } from "@/graphql/codegen/graphql"
import useDebounce from "@/utils/useDebounce"

interface UseAdjustmentLotSelectionParams {
  productExternalSystemId: string
  warehouseAreaExternalSystemId: string
}

interface UseAdjustmentLotSelectionReturn {
  palletGroups: InventoryPalletGroup[]
  isFetching: boolean
  selectedLotIds: Set<string>
  selectedLots: InventoryLotWithPallet[]
  untestedLots: InventoryLotReference[]
  sortBy: InventoryPalletGroupsSortField
  sortDirection: SortingOrder
  validateLotsTested: () => boolean
  handleSearchChange: (value: string) => void
  handleSortChange: (field: InventoryPalletGroupsSortField, direction: SortingOrder) => void
  handleSelectionChange: (lotIds: Set<string>) => void
  handleSelectionRemove: (lotId: string) => void
  handleSelectionReset: () => void
  handleUntestedLotsReset: () => void
}

export const useAdjustmentLotSelection = ({
  productExternalSystemId,
  warehouseAreaExternalSystemId,
}: UseAdjustmentLotSelectionParams): UseAdjustmentLotSelectionReturn => {
  const [selectedLotIds, setSelectedLotIds] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState("")
  const [sortBy, setSortBy] = useState<InventoryPalletGroupsSortField>(InventoryPalletGroupsSortField.ExpirationDate)
  const [sortDirection, setSortDirection] = useState<SortingOrder>(SortingOrder.Asc)

  const debouncedSearch = useDebounce(search, DEBOUNCE_DELAY_MS)

  const { palletGroups, isFetching } = useGetInventoryPalletGroups({
    productExternalSystemId,
    warehouseAreaExternalSystemId,
    search: debouncedSearch || undefined,
    sortBy,
    sortDirection,
  })

  const selectedLots = useMemo(
    () =>
      palletGroups.flatMap(({ lots, ...pallet }) =>
        lots.filter((lot) => selectedLotIds.has(lot.rawInventoryId)).map((lot) => ({ ...lot, pallet }))
      ),
    [palletGroups, selectedLotIds]
  )

  const { untestedLots, validateLotsTested, handleUntestedLotsReset } = useUntestedLotsValidation(selectedLots)

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
  }, [])

  const handleSortChange = useCallback((field: InventoryPalletGroupsSortField, direction: SortingOrder) => {
    setSortBy(field)
    setSortDirection(direction)
  }, [])

  const handleSelectionChange = useCallback((lotIds: Set<string>) => {
    setSelectedLotIds(lotIds)
  }, [])

  const handleSelectionRemove = useCallback((lotId: string) => {
    setSelectedLotIds((prev) => {
      const next = new Set(prev)
      next.delete(lotId)
      return next
    })
  }, [])

  const handleSelectionReset = useCallback(() => {
    setSelectedLotIds(new Set())
  }, [])

  return {
    palletGroups,
    isFetching,
    selectedLotIds,
    selectedLots,
    sortBy,
    sortDirection,
    untestedLots,
    handleSearchChange,
    handleSortChange,
    handleSelectionChange,
    handleSelectionRemove,
    handleSelectionReset,
    validateLotsTested,
    handleUntestedLotsReset,
  }
}
